import React, { Component } from 'react'
import {
    Alert,
    Tooltip,
    FormControl,
    Select,
    MenuItem,
    IconButton,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faFileCsv } from '@fortawesome/pro-duotone-svg-icons/faFileCsv'
import { faFileDownload } from '@fortawesome/pro-duotone-svg-icons/faFileDownload'
import { faFileExport } from '@fortawesome/pro-duotone-svg-icons/faFileExport'
import { faFilePdf } from '@fortawesome/pro-duotone-svg-icons/faFilePdf'
import { faFileWord } from '@fortawesome/pro-duotone-svg-icons/faFileWord'
import { faPlay } from '@fortawesome/pro-duotone-svg-icons/faPlay'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'

import { CSVLink } from 'react-csv'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import AppDialog from '@/Components/AppDialog/AppDialog'
import {
    StyledButton,
    ToolbarButton,
} from '@/Components/Elements/StyledComponents/Buttons'
import ExportFieldsTable from './Components/ExportFieldsTable'
import KedocxTable from './Components/KedocxTable'
import {
    ContentDossierExportProps,
    ContentDossierExportState,
    FetchFilter,
    FieldItem,
    KedocxItem,
    CsvRow,
} from './types'

class ContentDossierExport extends Component<
    ContentDossierExportProps,
    ContentDossierExportState
> {
    constructor(props: ContentDossierExportProps) {
        super(props)

        this.state = {
            csvData: [],
            csvHeaders: [],
            fields: [],
            metaFields: [],
            fileType: undefined,
            kedocx: [],
            loading: false,
            selectedFields: [],
            running: false,
            finished: false,
            total: 0,
            done: 0,
            search: '',
            showCSVModal: false,
            showExportSuccess: false,
            optionItemsId: [],
            removeHtml: true,
            showFullModal: false,
            selected: '',
        }
    }

    getMultiContent = (multiContent: any[], idLinkDossier: string[]) => {
        if (multiContent.length <= 0) {
            return ''
        }

        // User field
        if (multiContent[0].team && multiContent[0].text) {
            return Array.prototype.map
                .call(multiContent, function (item) {
                    return item.text
                })
                .join('|')
        }

        // Link field
        if (
            Array.isArray(idLinkDossier) &&
            idLinkDossier.includes(multiContent[0].id.toString())
        ) {
            return Array.prototype.map
                .call(multiContent, function (item) {
                    return item.id
                })
                .join('|')
        } else if (multiContent[0].translations) {
            return Array.prototype.map
                .call(multiContent, function (item) {
                    return item.translations[0].text
                })
                .join('|')
        } else if (multiContent[0].summary) {
            return Array.prototype.map
                .call(multiContent, function (item) {
                    return item.summary
                })
                .join('|')
        }
        return ''
    }

    isAllowedKedocx = (kedocxRoles: { id: string }[]) => {
        if (
            this.props.kedo.user().isSuperAdmin() ||
            this.props.kedo.env().isEnvironmentAdmin()
        ) {
            return true
        }
        let availableKedocx = false
        const roles = this.props.kedo.env().getCurrentEnvironmentRoles()
        roles.forEach((rol: { id: string }) => {
            kedocxRoles.forEach((krol) => {
                if (rol.id === krol.id) {
                    availableKedocx = true
                }
            })
        })
        return availableKedocx
    }

    fetchKedocx = () => {
        const url = this.props.kedo.api().getKedocxEndpoint()
        const filters: { params: Record<string, any> } = {
            params: {
                environment: this.props.kedo.env().getEnvironment().id,
                defDossier: this.props.defDossierId,
            },
        }

        if (this.props.dossier && this.props.dossier.def_dosssier_id) {
            filters.params.defDossier = this.props.dossier.def_dosssier_id
        }

        if (this.state.search !== null && this.state.search.length > 0) {
            filters.params.search = this.state.search
        }

        this.setState({ loading: true })

        this.props.kedo
            .api()
            .get(url, filters)
            .then((response) => {
                const kedocxs: KedocxItem[] = []
                response.data.results.forEach((kedocx: KedocxItem) => {
                    if (
                        kedocx?.roles &&
                        kedocx.roles.length > 0 &&
                        this.isAllowedKedocx(kedocx.roles)
                    ) {
                        kedocxs.push(kedocx)
                    } else if (!kedocx?.roles || kedocx.roles.length <= 0) {
                        kedocxs.push(kedocx)
                    }
                })

                this.setState({
                    kedocx: kedocxs,
                    loading: false,
                })
            })
    }

    fetchFields = () => {
        if (this.state.loading) {
            return
        }

        const kedo = this.props.kedo

        this.setState({ loading: true })

        const params: { params: Record<string, any> } = {
            params: {
                displayPosition: 'main_content',
                defDossier: this.props.defDossierId,
            },
        }

        if (this.state.search !== null && this.state.search.length > 0) {
            params.params.search = this.state.search
        }

        kedo.api()
            .getCached(kedo.api().getDisplayItemEndpoint(), params)
            .then((response) => {
                this.setState({
                    fields: response.data.results,
                    loading: false,
                })
            })
    }

    getAllData = async () => {
        const filter: FetchFilter = { ...this.props.filter(), page: -1 }
        this.setState({ running: true })
        await this.fetchAllItems(filter, undefined, true)
    }

    fetchAllItems = async (
        filter: FetchFilter,
        headers?: FieldItem[],
        first?: boolean
    ) => {
        if (this.props.all === false || !this.props.filter) {
            return (
                <Alert severity={'error'}>
                    {this.props.kedo.t('Something went wrong')}
                </Alert>
            )
        }
        const kedo = this.props.kedo
        filter.page = filter.page + 1
        const response = await kedo
            .api()
            .get(kedo.api().getContentDossierEndpoint(), { params: filter })
        const csvData = this.state.csvData

        if (first === true) {
            this.setState({ total: response.data.pager.items })
        }

        if (!headers) {
            headers = this.state.selectedFields
            const headerRow = []
            headerRow.push({ key: 'content_dossier_id', label: 'dossier_id' })
            headers.map((item) => {
                headerRow.push({
                    key: item.id.toString(),
                    label: kedo.env().translateItem(item, 'displayitem'),
                })
            })
            headerRow.push({ key: 'created_by', label: 'created_by' })
            headerRow.push({ key: 'created_at', label: 'created_at' })
            headerRow.push({ key: 'modified_by', label: 'modified_by' })
            headerRow.push({ key: 'modified_at', label: 'modified_at' })
            this.setState({ csvHeaders: headerRow })
        }

        //let selectedDossiers = response.data.results.dossiers.map(dossier => dossier.id);
        let counter = 0

        const forLoop = async () => {
            for (
                let index = 0;
                index < response.data.results.dossiers.length;
                index++
            ) {
                const listDossier = response.data.results.dossiers[index]
                const contentDossierResponse = await kedo
                    .api()
                    .get(
                        kedo.api().getContentDossierEndpoint() +
                            '/' +
                            listDossier.id
                    )
                const dossierContent = contentDossierResponse.data.content
                const dossierRow: Record<string, any> = {}
                dossierRow['content_dossier_id'] = listDossier.id

                const optionItemsIdCopy = [...this.state.optionItemsId]

                headers.forEach((header) => {
                    let contentItem = dossierContent[header.id]

                    if (
                        typeof contentItem === 'string' &&
                        this.state.removeHtml
                    ) {
                        const doc = new DOMParser().parseFromString(
                            contentItem,
                            'text/html'
                        )
                        contentItem = doc.body.textContent || ''
                    }

                    let date: string | false = false
                    const localOptionItemsIdCopy = optionItemsIdCopy.includes(
                        header.id.toString()
                    )
                        ? [header.id.toString()]
                        : []

                    for (let i = 0; i < localOptionItemsIdCopy.length; i++) {
                        const element = localOptionItemsIdCopy[i]
                        const dataId = headers.find(
                            (h) => h.id.toString() === element
                        )

                        if (
                            dataId?.id &&
                            contentItem?.length > 0 &&
                            contentItem[0]?.id
                        ) {
                            localOptionItemsIdCopy[i] =
                                contentItem[0].id.toString()
                        }
                    }

                    if (
                        header?.def_dossier_def_field?.def_field.settings
                            ?.withtime === true
                    ) {
                        const parsedDate = new Date(contentItem)

                        const day = parsedDate.getDate()
                        const month = parsedDate.getMonth() + 1
                        const year = parsedDate.getFullYear()
                        const hours = parsedDate
                            .getHours()
                            .toString()
                            .padStart(2, '0')
                        const minutes = parsedDate
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')
                        const seconds = parsedDate
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')

                        date = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
                    }

                    if (contentItem) {
                        if (Array.isArray(contentItem)) {
                            dossierRow[header.id.toString()] =
                                this.getMultiContent(
                                    contentItem,
                                    localOptionItemsIdCopy
                                )
                        } else {
                            dossierRow[header.id.toString()] = date
                                ? date
                                : contentItem.replace(/"/g, '""')
                        }
                    } else {
                        dossierRow[header.id.toString()] = ''
                    }
                })

                this.setState({ optionItemsId: optionItemsIdCopy })

                dossierRow['created_by'] =
                    listDossier.created_by && listDossier.created_by.username
                        ? listDossier.created_by.username
                        : ''
                dossierRow['created_at'] = listDossier.created_at
                    ? listDossier.created_at
                    : ''
                dossierRow['modified_by'] =
                    listDossier.modified_by && listDossier.modified_by.username
                        ? listDossier.modified_by.username
                        : ''
                dossierRow['modified_at'] = listDossier.modified_at
                    ? listDossier.modified_at
                    : ''
                csvData.push(dossierRow)
                counter = counter + 1

                this.setState({ done: this.state.done + 1 })
            }
        }

        await forLoop()

        if (response.data.pager.current >= response.data.pager.pages) {
            this.setState({
                running: false,
                csvData: csvData,
                finished: true,
            })
        } else {
            this.setState({
                csvData: csvData,
            })
            await this.fetchAllItems(filter, headers)
        }
    }

    fetchSingularItems = (filter?: FetchFilter, headers?: FieldItem[]) => {
        this.setState({ running: true })

        const csvData = this.state.csvData
        if (!headers) {
            headers = this.state.selectedFields

            const headerRow = []
            headerRow.push({ key: 'content_dossier_id', label: 'dossier_id' })
            headers.map((item) => {
                headerRow.push({
                    key: item.id.toString(),
                    label: this.props.kedo
                        .env()
                        .translateItem(item, 'displayitem'),
                })
            })
            headerRow.push({ key: 'created_by', label: 'created_by' })
            headerRow.push({ key: 'created_at', label: 'created_at' })
            headerRow.push({ key: 'modified_by', label: 'modified_by' })
            headerRow.push({ key: 'modified_at', label: 'modified_at' })

            this.setState({ csvHeaders: headerRow })
        }

        const selectedDossiers = this.props.selected || []
        const allSelectedDossiers = selectedDossiers.length
        let counter = 0
        let x

        for (x of selectedDossiers) {
            this.props.kedo
                .api()
                .get(
                    this.props.kedo.api().getContentDossierEndpoint() + '/' + x
                )
                .then((response) => {
                    const { content: dossierContent, dossier: dossierItem } =
                        response.data
                    const dossierRow: CsvRow = {
                        content_dossier_id: dossierItem.id,
                    }
                    const optionItemsIdCopy = [...this.state.optionItemsId]
                    dossierRow['content_dossier_id'] = dossierItem.id

                    headers.forEach((header) => {
                        let contentItem = dossierContent[header.id]

                        if (
                            typeof contentItem === 'string' &&
                            this.state.removeHtml
                        ) {
                            const doc = new DOMParser().parseFromString(
                                contentItem,
                                'text/html'
                            )
                            contentItem = doc.body.textContent || ''
                        }

                        const localOptionItemsIdCopy =
                            optionItemsIdCopy.includes(header.id.toString())
                                ? [header.id.toString()]
                                : []

                        for (
                            let i = 0;
                            i < localOptionItemsIdCopy.length;
                            i++
                        ) {
                            const element = localOptionItemsIdCopy[i]

                            const dataId = headers.find(
                                (h) => h.id.toString() === element
                            )

                            if (dataId?.id && contentItem[0]?.id) {
                                localOptionItemsIdCopy[i] =
                                    contentItem[0].id.toString()
                            }
                        }

                        if (contentItem) {
                            if (Array.isArray(contentItem)) {
                                dossierRow[header.id.toString()] =
                                    this.getMultiContent(
                                        contentItem,
                                        localOptionItemsIdCopy
                                    )
                            } else {
                                dossierRow[header.id.toString()] = contentItem
                            }
                        } else {
                            const metaField = this.state.metaFields.find(
                                (item) => item.id === header.id
                            )
                            if (metaField) {
                                dossierRow[metaField.id] =
                                    typeof dossierItem[metaField.id] == 'object'
                                        ? dossierItem[metaField.id].username
                                        : dossierItem[metaField.id]
                            } else {
                                dossierRow[header.id.toString()] = ''
                            }
                        }
                    })

                    this.setState({ optionItemsId: optionItemsIdCopy })
                    dossierRow['created_by'] =
                        dossierItem.created_by &&
                        dossierItem.created_by.username
                            ? dossierItem.created_by.username
                            : ''
                    dossierRow['created_at'] = dossierItem.created_at
                        ? dossierItem.created_at
                        : ''
                    dossierRow['modified_by'] =
                        dossierItem.modified_by &&
                        dossierItem.modified_by.username
                            ? dossierItem.modified_by.username
                            : ''
                    dossierRow['modified_at'] = dossierItem.modified_at
                        ? dossierItem.modified_at
                        : ''

                    csvData.push(dossierRow)
                    counter = counter + 1

                    if (counter === allSelectedDossiers) {
                        this.setState({
                            running: false,
                            csvData: csvData,
                            finished: true,
                        })
                    }
                })
        }
    }

    isAllowed = (credential) => {
        return this.props.kedo
            .env()
            .isAllowedDefDossier(credential, this.props.defDossierId)
    }

    runExport = (fileType) => {
        this.setState({ errorkedocx: null })

        if (!this.props.selected || !this.state.selected) {
            this.setState({
                errorkedocx: this.props.kedo.t('No files selected'),
            })
            return
        }

        const extra = '?contentDossierIds[]=' + this.props.selected
        const type = '&outputType=' + fileType
        const baseUrl = this.props.kedo.api().getBaseUrl()
        window.open(
            baseUrl + '/kedocx/generate/' + this.state.selected + extra + type
        )

        this.setState({ showExportSuccess: true })
        setTimeout(() => {
            this.setState({ showExportSuccess: false })
        }, 10000)
    }

    renderSuccessAlert() {
        return (
            <Alert
                key={'success'}
                severity={'success'}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pr: 1,
                    '& svg': {
                        marginLeft: 'auto',
                        cursor: 'pointer',
                        marginTop: '5px',
                        float: 'right',
                    },
                }}
            >
                &nbsp; {this.props.kedo.t('export_succesfully')}
                <FontAwesomeIcon
                    icon={faTimes}
                    onClick={() => this.setState({ showExportSuccess: false })}
                />
            </Alert>
        )
    }

    renderCsvSettings = () => {
        const { total, done, running, finished } = this.state
        const { all, selected, kedo } = this.props
        const percNum: number = total > 0 ? (done / total) * 100 : 0
        const percWidth: string = percNum.toFixed(3) + '%'

        return (
            <div>
                {(all || (selected && selected.length > 3)) && (
                    <Alert severity="info">
                        &nbsp; {kedo.t('export_all_dossiers')}
                    </Alert>
                )}

                {this.getFields()}

                {running && !finished && (
                    <div className="progress">
                        <div
                            className="progress-bar"
                            style={{ width: percWidth }}
                            role="progressbar"
                            aria-valuenow={percNum}
                            aria-valuemin={0}
                            aria-valuemax={total}
                        >
                            {done} / {total}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    handleFilterChange = (event: any) => {
        this.setState({
            search: event.target.value,
        })
    }

    handleSearchFields = (event: any) => {
        if (event.key !== 'Enter') {
            return
        }

        this.setState(
            { search: event.target.value.toString() },
            this.fetchFields
        )
    }

    handleSearchKedocx = (event: any) => {
        if (event.key !== 'Enter') {
            return
        }

        this.setState(
            { search: event.target.value.toString() },
            this.fetchKedocx
        )
    }

    resetButtonFields = () => {
        return (
            <IconButton
                onClick={() => {
                    this.setState({ search: '', fields: [] }, this.fetchFields)
                }}
                size="small"
                sx={{ ml: 0.5 }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </IconButton>
        )
    }

    resetButtonKedocx = () => {
        return (
            <IconButton
                onClick={() => {
                    this.setState({ search: '', fields: [] })
                    setTimeout(() => this.fetchKedocx(), 100)
                }}
                size="small"
                sx={{ ml: 0.5 }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </IconButton>
        )
    }

    handleChangeItemsId = (event: any) => {
        const selectedValue = event.target.value

        this.setState((prevState) => {
            if (selectedValue.startsWith('summary-')) {
                const idToRemove = selectedValue.split('summary-')[1]

                return {
                    optionItemsId: prevState.optionItemsId.filter(
                        (id) => id !== idToRemove
                    ),
                }
            } else {
                return {
                    optionItemsId: [...prevState.optionItemsId, selectedValue],
                }
            }
        })
    }

    renderItemsId = (dataSelect: string | number) => {
        const summaryValue = `summary-${dataSelect}`
        return (
            <FormControl size="small" fullWidth>
                <Select
                    size="small"
                    defaultValue={summaryValue}
                    onChange={this.handleChangeItemsId}
                    inputProps={{ 'aria-label': 'multiple select ids' }}
                >
                    <MenuItem value={summaryValue}>
                        {this.props.kedo.t('Summary')}
                    </MenuItem>

                    <MenuItem value={dataSelect}>
                        {this.props.kedo.t('Select id')}
                    </MenuItem>
                </Select>
            </FormControl>
        )
    }

    getFields() {
        return (
            <ExportFieldsTable
                kedo={this.props.kedo}
                fields={this.state.fields}
                metaFields={this.state.metaFields}
                selectedFields={this.state.selectedFields}
                loading={this.state.loading}
                fileType={this.state.fileType}
                removeHtml={this.state.removeHtml}
                searchValue={this.state.search}
                onToggleRemoveHtml={(checked) =>
                    this.setState({ removeHtml: checked })
                }
                onChangeFields={this.onChangeFields}
                selectAllFields={this.selectAllFields}
                onSearchChange={this.handleFilterChange}
                handleSearchFields={this.handleSearchFields}
                resetButtonFields={this.resetButtonFields}
                renderItemsId={this.renderItemsId}
                getItemMode={this.getItemMode}
            />
        )
    }

    getItemMode = (item) => {
        let view = this.props.kedo.t('Unknown')

        if (item.view === 'show') {
            view = this.props.kedo.t('Show')
        }

        if (item.view === 'show/edit') {
            view = this.props.kedo.t('Show') + '/' + this.props.kedo.t('Edit')
        }

        return view
    }

    onChangeFields = (event) => {
        if (this.state.finished) {
            this.setState({ finished: false })
        }

        let fields = this.state.selectedFields
        const fieldId = parseInt(event.target.value)
        const checked = event.target.checked

        fields = fields.filter(
            (item) =>
                item.id !== fieldId.toString() && item.id !== event.target.value
        )

        if (checked) {
            let fieldToAdd: FieldItem | undefined

            if (!fieldId) {
                fieldToAdd = this.state.metaFields.find(
                    (item) => item.id === event.target.value
                )
            } else {
                fieldToAdd = this.state.fields.find(
                    (item) => item.id === fieldId.toString()
                )
            }

            if (fieldToAdd) {
                fields.push(fieldToAdd)
            }
        }

        this.setState({ selectedFields: fields })
    }

    selectAllFields = (event) => {
        if (this.state.finished) {
            this.setState({ finished: false })
        }

        const fields = []
        const checked = event.target.checked

        if (checked) {
            this.state.fields.map((item) => fields.push(item))
            this.state.metaFields.map((item) => fields.push(item))
            this.setState({ selectedFields: fields })
        } else {
            this.setState({ selectedFields: [] })
        }
    }

    onClose = () => {
        if (this.props.showModal) {
            this.props.onCancel?.()
        }
        this.setState({
            csvHeaders: [],
            csvData: [],
            fileType: undefined,
            kedocx: [],
            search: '',
            selectedFields: [],
            showCSVModal: false,
            showFullModal: false,
            optionItemsId: [],
        })
    }

    toggleSettings = () => {
        if (this.state.fileType === 'csv') {
            return this.renderCsvSettings()
        } else if (
            this.state.fileType === 'pdf' ||
            this.state.fileType === 'docx'
        ) {
            return this.renderKedocxs()
        }
    }

    onChangeKedocx = (event) => {
        this.setState({ selected: event.target.value })
    }

    componentDidMount() {
        if (
            this.props.showModal &&
            this.props.selected &&
            this.props.selected.length > 1
        ) {
            this.fetchFields()
        }
    }

    renderKedocxs() {
        if (this.state.loading) {
            return <LoadingDefault />
        }

        return (
            <KedocxTable
                kedo={this.props.kedo}
                kedocx={this.state.kedocx}
                selected={parseInt(this.state.selected)}
                errorkedocx={this.state.errorkedocx}
                fileType={this.state.fileType}
                onExport={() => this.runExport(this.state.fileType)}
                searchValue={this.state.search}
                handleFilterChange={this.handleFilterChange}
                handleSearchKedocx={this.handleSearchKedocx}
                onResetSearch={this.resetButtonKedocx}
                onChangeKedocx={this.onChangeKedocx}
                resetButtonKedocx={this.resetButtonKedocx}
            />
        )
    }

    render() {
        const kedo = this.props.kedo
        if (
            (this.props.showModal === true &&
                this.props.selected !== undefined &&
                this.props.selected.length > 1) ||
            this.state.showCSVModal
        ) {
            return (
                <AppDialog
                    aria-labelledby="contained-modal-title-vcenter"
                    open
                    maxWidth={'lg'}
                    fullWidth
                    topAlign
                    title={
                        this.props.all
                            ? kedo.t('Export all files')
                            : kedo.t('export_selected')
                    }
                    onClose={this.onClose}
                    headerClose={this.onClose}
                    contentProps={{ dividers: true }}
                    actions={
                        <>
                            {!this.state.running && !this.state.finished ? (
                                <StyledButton
                                    onClick={() => {
                                        if (
                                            this.props.selected &&
                                            this.props.selected.length > 0
                                        ) {
                                            this.fetchSingularItems()
                                        } else {
                                            this.getAllData()
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlay} />
                                    &nbsp; {kedo.t('Prepare file')}
                                </StyledButton>
                            ) : null}
                            {this.state.finished && (
                                <div>
                                    <CSVLink
                                        enclosingCharacter={'"'}
                                        data={this.state.csvData}
                                        filename={`${this.props.defDossierId}_${
                                            this.props.defDossier
                                                ? this.props.kedo.translateItem(
                                                      this.props.defDossier,
                                                      'defdossier',
                                                      true
                                                  )
                                                : 'export'
                                        }_${Date.now()}.csv`}
                                        headers={this.state.csvHeaders}
                                    >
                                        <StyledButton onClick={this.onClose}>
                                            <FontAwesomeIcon
                                                icon={faFileDownload}
                                            />
                                            &nbsp; {kedo.t('Download')}
                                        </StyledButton>
                                    </CSVLink>
                                </div>
                            )}
                            <StyledButton
                                onClick={() => {
                                    if (this.props.showModal) {
                                        this.props.onCancel?.() // safely call only if defined
                                    }

                                    this.setState({
                                        showCSVModal: false,
                                        fileType: undefined,
                                        selectedFields: [],
                                    })
                                }}
                                color={'secondary'}
                                title={'Cancel'}
                            >
                                {kedo.t('Cancel')}
                            </StyledButton>
                        </>
                    }
                >
                    <div>
                        {this.state.showExportSuccess
                            ? this.renderSuccessAlert()
                            : null}
                        {this.renderCsvSettings()}
                    </div>
                </AppDialog>
            )
        }

        if (this.props.showModal === true || this.state.showFullModal) {
            return (
                <AppDialog
                    aria-labelledby="contained-modal-title-vcenter"
                    open
                    maxWidth={'lg'}
                    fullWidth
                    topAlign
                    title={kedo.t('export_selected')}
                    onClose={this.onClose}
                    headerClose={this.onClose}
                    contentProps={{ dividers: true }}
                    actions={
                        <>
                            {this.state.fileType === 'csv' &&
                            !this.state.running &&
                            !this.state.finished ? (
                                <StyledButton
                                    onClick={() => {
                                        if (
                                            this.props.selected &&
                                            this.props.selected.length > 0
                                        ) {
                                            this.fetchSingularItems()
                                        } else {
                                            this.getAllData()
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlay} />
                                    &nbsp; {kedo.t('Prepare file')}
                                </StyledButton>
                            ) : null}
                            {this.state.fileType === 'csv' &&
                            this.state.finished ? (
                                <CSVLink
                                    data={this.state.csvData}
                                    filename={`${this.props.defDossierId}_${
                                        this.props.defDossier
                                            ? this.props.kedo.translateItem(
                                                  this.props.defDossier,
                                                  'defdossier',
                                                  true
                                              )
                                            : 'export'
                                    }_${Date.now()}.csv`}
                                    headers={this.state.csvHeaders}
                                >
                                    <StyledButton onClick={this.onClose}>
                                        <FontAwesomeIcon
                                            icon={faFileDownload}
                                        />
                                        &nbsp; {kedo.t('Download')}
                                    </StyledButton>
                                </CSVLink>
                            ) : null}
                            <StyledButton
                                onClick={this.onClose}
                                color={'secondary'}
                                title={'Cancel'}
                            >
                                {kedo.t('Cancel')}
                            </StyledButton>
                        </>
                    }
                >
                    <div>
                        {this.state.showExportSuccess
                            ? this.renderSuccessAlert()
                            : null}
                        <h4>{kedo.t('Select type')}</h4>
                        {this.props.disablePDF !== true &&
                            (this.isAllowed('export') ||
                                this.isAllowed('export_pdf')) && (
                                <>
                                    <StyledButton
                                        variant={
                                            this.state.fileType === 'pdf'
                                                ? 'contained'
                                                : 'outlined'
                                        }
                                        color={'secondary'}
                                        invert={this.state.fileType !== 'pdf'}
                                        onClick={() => {
                                            this.setState({
                                                fileType: 'pdf',
                                                search: '',
                                            })
                                            this.fetchKedocx()
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faFilePdf} />
                                        &nbsp; PDF
                                    </StyledButton>
                                </>
                            )}
                        {this.props.disableWord !== true &&
                            (this.isAllowed('export') ||
                                this.isAllowed('export_word')) && (
                                <>
                                    <StyledButton
                                        variant={
                                            this.state.fileType === 'docx'
                                                ? 'contained'
                                                : 'outlined'
                                        }
                                        color="secondary"
                                        invert={this.state.fileType !== 'docx'}
                                        onClick={() => {
                                            this.setState({
                                                fileType: 'docx',
                                                search: '',
                                            })
                                            this.fetchKedocx()
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faFileWord} />
                                        &nbsp; Word (.docx)
                                    </StyledButton>
                                    &nbsp;
                                </>
                            )}

                        {this.isAllowed('export') ||
                        this.isAllowed('export_csv') ? (
                            <StyledButton
                                variant={
                                    this.state.fileType === 'csv'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color="secondary"
                                invert={this.state.fileType !== 'csv'}
                                onClick={() => {
                                    this.setState({
                                        fileType: 'csv',
                                        search: '',
                                        kedocx: [],
                                    })
                                    this.fetchFields()
                                }}
                            >
                                <FontAwesomeIcon icon={faFileCsv} />
                                &nbsp; Excel (CSV)
                            </StyledButton>
                        ) : null}
                        <br />
                        <br />
                        {this.toggleSettings()}
                    </div>
                </AppDialog>
            )
        }

        if (this.props.mode === 'all') {
            return (
                <Tooltip title={kedo.t('CSV Export')} placement="top">
                    <ToolbarButton
                        dense={this.props.size === 'sm'}
                        onClick={() => {
                            this.setState({ showCSVModal: true })
                            this.fetchFields()
                        }}
                        variant={'outlined'}
                        color="secondary"
                    >
                        <FontAwesomeIcon icon={faFileExport} />
                    </ToolbarButton>
                </Tooltip>
            )
        } else if (this.props.mode === 'dossier') {
            return (
                <ToolbarButton
                    dense={this.props.size === 'sm'}
                    onClick={() => this.setState({ showFullModal: true })}
                    variant="outlined"
                    title="Export"
                >
                    <FontAwesomeIcon icon={faFileExport} />
                </ToolbarButton>
            )
        } else if (
            this.props.selected !== undefined &&
            this.props.selected.length === 1
        ) {
            return (
                <ToolbarButton
                    dense={this.props.size === 'sm'}
                    onClick={() => this.setState({ showFullModal: true })}
                    title="Export"
                    variant={'outlined'}
                    color="secondary"
                >
                    <FontAwesomeIcon icon={faFileExport} />
                    &nbsp; {kedo.t('Export')}
                </ToolbarButton>
            )
        } else {
            return (
                <ToolbarButton
                    dense={this.props.size === 'sm'}
                    onClick={() => {
                        this.setState({ showCSVModal: true })
                        this.fetchFields()
                    }}
                    title="Export"
                    variant={'outlined'}
                    color="secondary"
                >
                    <FontAwesomeIcon icon={faFileCsv} />
                    &nbsp; {kedo.t('CSV Export')}
                </ToolbarButton>
            )
        }
    }
}

export default ContentDossierExport
